import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from './AuthContext';
import './Schedule.css';

const Schedule = () => {
  const navigate = useNavigate();
  const { isGuest } = useAuth();
  const [dates, setDates] = useState([]);
  const [currentDate, setCurrentDate] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(false);

  // 检查登录状态
  const checkLogin = () => {
    if (isGuest) {
      navigate('/login');
      return false;
    }
    return true;
  };
  
  const [newDate, setNewDate] = useState('');
  const [newStartTime, setNewStartTime] = useState('08:30');
  const [duration, setDuration] = useState('');
  const [content, setContent] = useState('');
  const [generatedText, setGeneratedText] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editDuration, setEditDuration] = useState('');
  const [editContent, setEditContent] = useState('');
  const [historyList, setHistoryList] = useState([]);
  const [scheduleTitle, setScheduleTitle] = useState(''); // 行程主题
  const [loadedHistoryId, setLoadedHistoryId] = useState(null); // 当前加载的历史记录ID

  // 根据当前访问地址自动判断 API 地址
  const API_BASE_URL = process.env.REACT_APP_API_URL || 
    (window.location.hostname === 'localhost' ? 'http://localhost:8000' : window.location.origin);

  // 获取认证token
  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return {
      'Authorization': `Bearer ${token}`
    };
  };

  // 加载日期列表
  useEffect(() => {
    loadDates();
  }, []);

  // 当日期改变时加载当前行程
  useEffect(() => {
    if (currentDate) {
      loadCurrentSchedule();
    }
  }, [currentDate]);

  // 加载历史记录
  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    // 检查是否已登录
    const token = localStorage.getItem('access_token');
    if (!token) {
      console.log('未登录，跳过加载历史记录');
      setHistoryList([]);
      return;
    }
    
    try {
      const response = await axios.get(`${API_BASE_URL}/api/schedule/history`, {
        headers: getAuthHeaders()
      });
      setHistoryList(response.data.data || []);
    } catch (error) {
      console.error('加载历史记录失败:', error);
      // 如果是401错误，说明token无效，跳转到登录页
      if (error.response?.status === 401) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        navigate('/login');
      }
    }
  };

  const loadDates = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/schedule/dates`);
      setDates(response.data.dates || []);
      if (response.data.dates && response.data.dates.length > 0) {
        setCurrentDate(response.data.dates[0]);
      }
    } catch (error) {
      console.error('加载日期列表失败:', error);
    }
  };

  const loadCurrentSchedule = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/schedule/current`);
      setSchedule(response.data.schedule);
    } catch (error) {
      console.error('加载行程失败:', error);
    }
  };

  const handleAddDate = async () => {
    if (!checkLogin()) return;
    
    if (!newDate) {
      alert('请选择日期');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/schedule/add-date`, {
        date_str: newDate,
        start_time: newStartTime
      });
      await loadDates();
      setNewDate('');
      setNewStartTime('08:30');
      alert('日期添加成功');
    } catch (error) {
      alert(error.response?.data?.detail || '添加失败');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDate = async (dateStr) => {
    if (!window.confirm('确定要删除这个日期及其所有行程吗?')) {
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/schedule/delete-date/${dateStr}`);
      await loadDates();
      if (currentDate === dateStr) {
        setCurrentDate(dates[0] || null);
      }
      alert('日期删除成功');
    } catch (error) {
      alert(error.response?.data?.detail || '删除失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDate = async (dateStr) => {
    setCurrentDate(dateStr);
    try {
      await axios.post(`${API_BASE_URL}/api/schedule/set-current/${dateStr}`);
    } catch (error) {
      console.error('设置当前日期失败:', error);
    }
  };

  const handleAddItem = async () => {
    if (!checkLogin()) return;
    
    if (!duration || !content) {
      alert('请填写时长和内容');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/schedule/add-item`, {
        duration: parseInt(duration),
        content: content
      });
      await loadCurrentSchedule();
      setDuration('');
      setContent('');
    } catch (error) {
      alert(error.response?.data?.detail || '添加失败');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (index) => {
    if (!window.confirm('确定要删除这个行程事项吗?')) {
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/schedule/delete-item/${index}`);
      await loadCurrentSchedule();
    } catch (error) {
      alert(error.response?.data?.detail || '删除失败');
    } finally {
      setLoading(false);
    }
  };

  const handleMoveUp = async (index) => {
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/schedule/move-up/${index}`);
      await loadCurrentSchedule();
    } catch (error) {
      alert(error.response?.data?.detail || '上移失败');
    } finally {
      setLoading(false);
    }
  };

  const handleMoveDown = async (index) => {
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/schedule/move-down/${index}`);
      await loadCurrentSchedule();
    } catch (error) {
      alert(error.response?.data?.detail || '下移失败');
    } finally {
      setLoading(false);
    }
  };

  const handleInsertItem = async (index) => {
    const insertDuration = prompt('请输入时长（分钟）:');
    if (!insertDuration) return;
    
    const insertContent = prompt('请输入行程内容:');
    if (!insertContent) return;

    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/schedule/insert-item`, {
        index: index,
        duration: parseInt(insertDuration),
        content: insertContent
      });
      await loadCurrentSchedule();
    } catch (error) {
      alert(error.response?.data?.detail || '插入失败');
    } finally {
      setLoading(false);
    }
  };

  const handleEditItem = (index, item) => {
    setEditingIndex(index);
    setEditDuration(item.duration.toString());
    setEditContent(item.content);
  };

  const handleSaveEdit = async (index) => {
    if (!checkLogin()) return;
    
    if (!editDuration || !editContent) {
      alert('请填写完整信息');
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API_BASE_URL}/api/schedule/update-item`, {
        index: index,
        duration: parseInt(editDuration),
        content: editContent
      });
      await loadCurrentSchedule();
      setEditingIndex(null);
      setEditDuration('');
      setEditContent('');
    } catch (error) {
      alert(error.response?.data?.detail || '修改失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditDuration('');
    setEditContent('');
  };

  const handleEditStartTime = async () => {
    if (!currentDate) {
      alert('请先选择或添加日期');
      return;
    }
    if (!schedule) return;
    
    const newTime = prompt('请输入新的开始时间（格式：HH:MM，例如 08:30）:', schedule.start_time);
    if (!newTime) return;
    
    // 验证时间格式
    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
    if (!timeRegex.test(newTime)) {
      alert('时间格式不正确，请使用 HH:MM 格式（例如 08:30）');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/schedule/set-start-time`, {
        time_str: newTime
      });
      await loadCurrentSchedule();
      alert('开始时间修改成功');
    } catch (error) {
      alert(error.response?.data?.detail || '修改失败');
    } finally {
      setLoading(false);
    }
  };

  const handleTitleChange = async (e) => {
    const newTitle = e.target.value;
    setScheduleTitle(newTitle);
    // 同步更新到后端
    try {
      await axios.post(`${API_BASE_URL}/api/schedule/set-title`, {
        title: newTitle
      });
    } catch (error) {
      console.error('设置主题失败:', error);
    }
  };

  // 填充示例行程
  const handleFillExample = async () => {
    if (!window.confirm('填充示例将清空当前行程并填充示例数据，是否继续？')) {
      return;
    }

    setLoading(true);
    try {
      // 1. 清空当前数据
      await axios.post(`${API_BASE_URL}/api/schedule/clear-all`);
      
      // 2. 设置行程主题
      const exampleTitle = '关于赴广州开展考察调研的行程安排';
      setScheduleTitle(exampleTitle);
      await axios.post(`${API_BASE_URL}/api/schedule/set-title`, {
        title: exampleTitle
      });

      // 3. 添加日期（明天）
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const dateStr = tomorrow.toISOString().split('T')[0];
      
      await axios.post(`${API_BASE_URL}/api/schedule/add-date`, {
        date_str: dateStr,
        start_time: '08:30'
      });
      
      // 4. 选择该日期
      await loadDates();
      setCurrentDate(dateStr);
      
      // 5. 添加示例行程事项
      const exampleItems = [
        { duration: 30, content: '在单位集合，准备出发' },
        { duration: 60, content: '乘车前往广州（途中讨论调研方案）' },
        { duration: 90, content: '抵达广州，入住酒店' },
        { duration: 60, content: '午餐' },
        { duration: 120, content: '前往XX公司考察调研，听取工作汇报' },
        { duration: 60, content: '实地参观生产车间和研发中心' },
        { duration: 60, content: '晚餐' },
        { duration: 30, content: '返回酒店休息' }
      ];
      
      for (const item of exampleItems) {
        await axios.post(`${API_BASE_URL}/api/schedule/add-item`, {
          duration: item.duration,
          content: item.content
        });
      }
      
      // 6. 刷新数据
      await loadCurrentSchedule();
      await loadDates();
      
      alert('示例行程填充成功！');
    } catch (error) {
      console.error('填充示例失败:', error);
      alert(error.response?.data?.detail || '填充示例失败');
    } finally {
      setLoading(false);
    }
  };

  // 自动保存当前行程（用于生成文本/Word时调用）
  const autoSaveSchedule = async () => {
    if (dates.length === 0) return;
    
    // 检查是否已登录
    const token = localStorage.getItem('access_token');
    if (!token) {
      console.log('未登录，跳过自动保存');
      return;
    }
    
    try {
      const title = scheduleTitle || `行程安排_${new Date().toLocaleString('zh-CN', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).replace(/[\/\s:]/g, '-')}`;
      
      // 判断是更新还是创建
      if (loadedHistoryId) {
        // 更新已加载的历史记录
        await axios.put(`${API_BASE_URL}/api/schedule/history/${loadedHistoryId}?title=${encodeURIComponent(title)}`, {}, {
          headers: getAuthHeaders()
        });
        console.log('自动更新成功');
      } else {
        // 创建新的历史记录
        await axios.post(`${API_BASE_URL}/api/schedule/save?title=${encodeURIComponent(title)}`, {}, {
          headers: getAuthHeaders()
        });
        console.log('自动保存成功');
      }
      
      await loadHistory();
    } catch (error) {
      console.error('自动保存失败:', error);
      // 如果是401错误，跳转到登录页
      if (error.response?.status === 401) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return;
      }
      // 其他错误静默失败
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('确定要清空所有行程吗?此操作不可恢复!')) {
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/schedule/clear-all`);
      await loadDates();
      setSchedule(null);
      alert('所有行程已清空');
    } catch (error) {
      alert(error.response?.data?.detail || '清空失败');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateText = async () => {
    // 检查登录状态
    if (!checkLogin()) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/schedule/generate-text`);
      setGeneratedText(response.data.text);
      setShowPreview(true);
      // 自动保存
      await autoSaveSchedule();
    } catch (error) {
      alert(error.response?.data?.detail || '生成失败');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateWord = async () => {
    // 检查登录状态
    if (!checkLogin()) return;
    
    setLoading(true);
    try {
      // 调用生成Word文档的API
      const response = await axios.post(`${API_BASE_URL}/api/schedule/generate-word`);
      const downloadId = response.data.download_id;
      
      // 触发下载
      const downloadUrl = `${API_BASE_URL}/download/${downloadId}`;
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = '行程安排.docx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // 自动保存
      await autoSaveSchedule();
      
      alert('Word文档已生成并开始下载');
    } catch (error) {
      console.error('生成Word文档失败:', error);
      alert(error.response?.data?.detail || '生成Word文档失败');
    } finally {
      setLoading(false);
    }
  };

  // 快速保存（使用当前主题或生成默认标题）
  const handleQuickSave = async () => {
    // 检查登录状态
    if (!checkLogin()) return;
    
    if (dates.length === 0) {
      alert('请先添加日期');
      return;
    }
    
    setLoading(true);
    try {
      let title = scheduleTitle;
      if (!title) {
        title = `行程安排_${new Date().toLocaleString('zh-CN', { 
          year: 'numeric', 
          month: '2-digit', 
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        }).replace(/[\/\s:]/g, '-')}`;
      }
      
      await axios.post(`${API_BASE_URL}/api/schedule/set-title`, {
        title: title
      });
      
      // 判断是更新还是创建
      if (loadedHistoryId) {
        // 更新已加载的历史记录
        await axios.put(`${API_BASE_URL}/api/schedule/history/${loadedHistoryId}?title=${encodeURIComponent(title)}`, {}, {
          headers: getAuthHeaders()
        });
        alert('行程安排更新成功');
      } else {
        // 创建新的历史记录
        await axios.post(`${API_BASE_URL}/api/schedule/save?title=${encodeURIComponent(title)}`, {}, {
          headers: getAuthHeaders()
        });
        alert('行程安排保存成功');
      }
      
      await loadHistory();
    } catch (error) {
      alert(error.response?.data?.detail || '保存失败');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadHistory = async (historyId) => {
    if (!window.confirm('加载历史记录将覆盖当前行程，确定继续？')) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/schedule/history/${historyId}`, {
        headers: getAuthHeaders()
      });
      
      const data = response.data.data;
      const datesList = data.dates || [];
      setDates(datesList);
      
      if (datesList.length > 0) {
        // 选中第1天
        const firstDate = datesList[0];
        setCurrentDate(firstDate);
        
        // 加载第1天的行程
        if (data.schedule_data) {
          const scheduleData = JSON.parse(data.schedule_data);
          if (scheduleData.schedule_list && scheduleData.schedule_list.length > 0) {
            // 找到第1天的行程
            const firstDaySchedule = scheduleData.schedule_list.find(
              item => item.date_str === firstDate
            );
            if (firstDaySchedule) {
              setSchedule(firstDaySchedule.schedule);
            } else {
              setSchedule(null);
            }
          } else {
            setSchedule(null);
          }
        } else {
          setSchedule(data.schedule);
        }
      } else {
        setCurrentDate(null);
        setSchedule(null);
      }
      
      setScheduleTitle(data.title || ''); // 加载主题
      setLoadedHistoryId(historyId); // 记录加载的历史记录ID
      alert('历史记录加载成功');
    } catch (error) {
      if (error.response?.status === 401) {
        alert('登录已过期，请重新登录');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        navigate('/login');
      } else {
        alert(error.response?.data?.detail || '加载失败');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHistory = async (historyId) => {
    if (!window.confirm('确定要删除这条历史记录吗？')) {
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/schedule/history/${historyId}`, {
        headers: getAuthHeaders()
      });
      alert('历史记录删除成功');
      await loadHistory();
    } catch (error) {
      if (error.response?.status === 401) {
        alert('登录已过期，请重新登录');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        navigate('/login');
      } else {
        alert(error.response?.data?.detail || '删除失败');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const weekdays = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
    return `${dateStr} (${weekdays[date.getDay()]})`;
  };

  const calculateTimeRange = (startTime, duration, index, items) => {
    const [startHour, startMinute] = startTime.split(':').map(Number);
    let currentHour = startHour;
    let currentMinute = startMinute;

    // 计算之前所有事项的累计时间
    for (let i = 0; i < index; i++) {
      const totalMinutes = currentHour * 60 + currentMinute + items[i].duration;
      currentHour = Math.floor(totalMinutes / 60);
      currentMinute = totalMinutes % 60;
    }

    const totalMinutes = currentHour * 60 + currentMinute + duration;
    const endHour = Math.floor(totalMinutes / 60);
    const endMinute = totalMinutes % 60;

    return `${String(currentHour).padStart(2, '0')}:${String(currentMinute).padStart(2, '0')}--${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`;
  };

  return (
    <div className="schedule-container">
      <div className="schedule-content">
        {/* 左侧面板：历史记录和日期列表上下排列 */}
        <div className="left-panel">
          {/* 历史记录栏 */}
          <div className="history-panel">
            <div className="panel-header">
              <h3>历史记录</h3>
            </div>
            <div className="history-list">
              {historyList.length === 0 ? (
                <div className="empty-history">暂无历史记录</div>
              ) : (
                historyList.map((item) => (
                  <div key={item.id} className="history-item">
                    <div className="history-title-row">
                      <div className="history-title">{item.title}</div>
                      <div className="history-actions">
                        <button 
                          className="load-btn"
                          onClick={() => handleLoadHistory(item.id)}
                          disabled={!isGuest && loading}
                        >
                          加载
                        </button>
                        <button 
                          className="delete-history-btn"
                          onClick={() => handleDeleteHistory(item.id)}
                          disabled={!isGuest && loading}
                        >
                          删除
                        </button>
                      </div>
                    </div>
                    <div className="history-time">{item.updated_at || item.created_at}</div>
                  </div>
                ))
              )}
            </div>
          </div>
          
          {/* 日期列表 */}
          <div className="dates-panel">
            <div className="panel-header">
              <h3>日期列表</h3>
              <span className="date-count">{dates.length} 天</span>
            </div>

            <div className="add-date-form">
              <input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
              />
              <input
                type="time"
                value={newStartTime}
                onChange={(e) => setNewStartTime(e.target.value)}
              />
              <button 
                className="add-btn"
                onClick={handleAddDate}
                disabled={!isGuest && loading}
              >
                添加日期
              </button>
            </div>

            <div className="dates-list">
              {dates.map((date, index) => (
                <div
                  key={`${date}-${index}`}
                  className={`date-item ${currentDate === date ? 'active' : ''}`}
                  onClick={() => handleSelectDate(date)}
                >
                  <span className="date-text">{formatDate(date)}</span>
                  <button
                    className="delete-date-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteDate(date);
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
              {dates.length === 0 && (
                <div className="empty-state">暂无日期,请添加</div>
              )}
            </div>

            <div className="panel-actions">
              <button 
                className="action-btn danger"
                onClick={handleClearAll}
                disabled={!isGuest && (loading || dates.length === 0)}
              >
                清空所有
              </button>
            </div>
          </div>
        </div>

        {/* 右侧行程详情 */}
        <div className="schedule-panel">
          <div className="panel-header">
            <h3>行程详情</h3>
            <div className="header-actions">
              <button 
                className="fill-example-btn"
                onClick={handleFillExample}
                disabled={!isGuest && loading}
                title="填充示例行程"
              >
                📝 填充示例
              </button>
              {schedule && (
                <span 
                  className="start-time editable" 
                  onClick={handleEditStartTime}
                  title="点击修改开始时间"
                >
                  开始时间: {schedule.start_time}
                </span>
              )}
            </div>
          </div>

          {/* 行程主题 */}
          <div className="schedule-title-section">
            <input
              type="text"
              placeholder="请输入行程主题..."
              value={scheduleTitle}
              onChange={handleTitleChange}
              className="schedule-title-input"
            />
          </div>

          {schedule ? (
            <>
              <div className="add-item-form">
                <input
                  type="number"
                  placeholder="时长(分钟)"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  min="1"
                />
                <input
                  type="text"
                  placeholder="行程内容"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddItem()}
                />
                <button 
                  className="add-btn"
                  onClick={handleAddItem}
                  disabled={!isGuest && loading}
                >
                  添加事项
                </button>
              </div>

              <div className="items-list">
                {/* 桌面端表格布局 */}
                <table className="items-table">
                  <thead>
                    <tr>
                      <th width="50">序号</th>
                      <th width="130">时间段</th>
                      <th width="80">时长(分钟)</th>
                      <th>内容</th>
                      <th width="260">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.items.map((item, index) => (
                      <tr key={index}>
                        <td className="center">{index + 1}</td>
                        <td className="center">
                          {calculateTimeRange(schedule.start_time, item.duration, index, schedule.items)}
                        </td>
                        {editingIndex === index ? (
                          <>
                            <td className="center">
                              <input
                                type="number"
                                className="edit-input"
                                value={editDuration}
                                onChange={(e) => setEditDuration(e.target.value)}
                                placeholder="时长"
                                autoFocus
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="edit-input content-input"
                                value={editContent}
                                onChange={(e) => setEditContent(e.target.value)}
                                placeholder="内容"
                              />
                            </td>
                            <td className="center">
                              <div className="action-buttons">
                                <button
                                  className="save-btn"
                                  onClick={() => handleSaveEdit(index)}
                                  disabled={!isGuest && loading}
                                >
                                  保存
                                </button>
                                <button
                                  className="cancel-btn"
                                  onClick={handleCancelEdit}
                                  disabled={!isGuest && loading}
                                >
                                  取消
                                </button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td 
                              className="center editable-cell"
                              onClick={() => handleEditItem(index, item)}
                              title="点击编辑"
                            >
                              {item.duration}
                            </td>
                            <td 
                              className="editable-cell"
                              onClick={() => handleEditItem(index, item)}
                              title="点击编辑"
                            >
                              {item.content}
                            </td>
                            <td className="center">
                              <div className="action-buttons">
                                <button
                                  className="move-btn"
                                  onClick={() => handleMoveUp(index)}
                                  disabled={!isGuest && (loading || index === 0)}
                                  title="上移"
                                >
                                  ↑
                                </button>
                                <button
                                  className="move-btn"
                                  onClick={() => handleMoveDown(index)}
                                  disabled={!isGuest && (loading || index === schedule.items.length - 1)}
                                  title="下移"
                                >
                                  ↓
                                </button>
                                <button
                                  className="insert-btn"
                                  onClick={() => handleInsertItem(index)}
                                  disabled={!isGuest && loading}
                                  title="在此处插入"
                                >
                                  插入
                                </button>
                                <button
                                  className="delete-btn"
                                  onClick={() => handleDeleteItem(index)}
                                  disabled={!isGuest && loading}
                                >
                                  删除
                                </button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* 移动端卡片式布局 */}
                <div className="mobile-items-list">
                  {schedule.items.map((item, index) => (
                    <div 
                      key={index} 
                      className={`mobile-item-card ${editingIndex === index ? 'mobile-edit-card' : ''}`}
                    >
                      {editingIndex === index ? (
                        // 编辑模式
                        <>
                          <div className="mobile-item-header">
                            <span className="mobile-item-index">第 {index + 1} 项</span>
                            <span className="mobile-item-time">
                              {calculateTimeRange(schedule.start_time, item.duration, index, schedule.items)}
                            </span>
                          </div>
                          <input
                            type="number"
                            className="mobile-edit-input"
                            value={editDuration}
                            onChange={(e) => setEditDuration(e.target.value)}
                            placeholder="时长（分钟）"
                            autoFocus
                          />
                          <input
                            type="text"
                            className="mobile-edit-input"
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            placeholder="行程内容"
                          />
                          <div className="mobile-item-actions">
                            <button
                              className="save-btn"
                              onClick={() => handleSaveEdit(index)}
                              disabled={!isGuest && loading}
                            >
                              保存
                            </button>
                            <button
                              className="cancel-btn"
                              onClick={handleCancelEdit}
                              disabled={!isGuest && loading}
                            >
                              取消
                            </button>
                          </div>
                        </>
                      ) : (
                        // 查看模式
                        <>
                          <div className="mobile-item-header">
                            <span className="mobile-item-index">第 {index + 1} 项</span>
                            <span className="mobile-item-time">
                              {calculateTimeRange(schedule.start_time, item.duration, index, schedule.items)}
                            </span>
                          </div>
                          <div className="mobile-item-content">{item.content}</div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <span className="mobile-item-duration">{item.duration} 分钟</span>
                          </div>
                          <div className="mobile-item-actions">
                            <button
                              className="move-btn"
                              onClick={() => handleMoveUp(index)}
                              disabled={!isGuest && (loading || index === 0)}
                              title="上移"
                            >
                              ↑ 上移
                            </button>
                            <button
                              className="move-btn"
                              onClick={() => handleMoveDown(index)}
                              disabled={!isGuest && (loading || index === schedule.items.length - 1)}
                              title="下移"
                            >
                              ↓ 下移
                            </button>
                            <button
                              className="insert-btn"
                              onClick={() => handleInsertItem(index)}
                              disabled={!isGuest && loading}
                            >
                              插入
                            </button>
                            <button
                              className="delete-btn"
                              onClick={() => handleDeleteItem(index)}
                              disabled={!isGuest && loading}
                            >
                              删除
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>

                {schedule.items.length === 0 && (
                  <div className="empty-state">暂无行程事项,请添加</div>
                )}
              </div>

              <div className="panel-actions">
                <button 
                  className="action-btn save-action"
                  onClick={handleQuickSave}
                  disabled={!isGuest && (loading || schedule.items.length === 0)}
                >
                  保存
                </button>
                <button 
                  className="action-btn primary"
                  onClick={handleGenerateText}
                  disabled={!isGuest && (loading || schedule.items.length === 0)}
                >
                  生成文本
                </button>
                <button 
                  className="action-btn success"
                  onClick={handleGenerateWord}
                  disabled={!isGuest && (loading || schedule.items.length === 0)}
                >
                  生成Word文档
                </button>
              </div>
            </>
          ) : (
            <div className="empty-state large">
              请先选择或添加日期
            </div>
          )}
        </div>
      </div>

      {/* 预览弹窗 */}
      {showPreview && (
        <div className="preview-modal">
          <div className="preview-content">
            <div className="preview-header">
              <h3>行程预览</h3>
              <button 
                className="close-btn"
                onClick={() => setShowPreview(false)}
              >
                ×
              </button>
            </div>
            <div className="preview-body">
              <pre>{generatedText}</pre>
            </div>
            <div className="preview-footer">
              <button 
                className="copy-btn"
                onClick={() => {
                  navigator.clipboard.writeText(generatedText);
                  alert('已复制到剪贴板');
                }}
              >
                复制文本
              </button>
              <button 
                className="close-preview-btn"
                onClick={() => setShowPreview(false)}
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Schedule;
